import { NOTE_CUT, SAFE_EFFECTS, TrackerNote, TrackerPattern, effectById, maxNoteFor, noteName, voiceLimit, LIMITS, MAX_NOTE } from "../../shared/tracker";
import { Step } from "../../shared/tracker_render";
import { byId } from "../dom";
import { t } from "../i18n";
import { previewNote } from "../tracker_player";
import { edit, state } from "../tracker_store";

// the notes tab: a grid of squares, four voices at a time on a phone and eight on a wide screen. tap a square to open the
// note sheet, or select one and play the computer keyboard like a piano. the green row follows the song while it plays

const LOWER = "zsxdcvgbhnjm,l.;/";
const UPPER = "q2w3er5t6y7ui9o0p";
const WHITE = [0, 2, 4, 5, 7, 9, 11];
const BLACK: Array<[number, number]> = [[1, 1], [3, 2], [6, 4], [8, 5], [10, 6]];
const KEY_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

let page = 0;
let playingRow = -1;
let earSnes = () => false;

const grid = () => byId("tk-grid");
const sheet = () => byId("tk-sheet");

export const pattern = (): TrackerPattern => state.song!.patterns[state.pat];
const perPage = (): number => (window.matchMedia("(min-width: 760px)").matches ? 8 : 4);
const voiceCount = (): number => LIMITS[state.song!.mode].voices;
const noteAt = (r: number, c: number): TrackerNote | undefined => pattern().notes.find((note) => note.r === r && note.c === c);

function pageCount(): number {
  return Math.max(1, Math.ceil(voiceCount() / perPage()));
}

function usedVoices(): Set<number> {
  const used = new Set<number>();
  state.song!.patterns.forEach((item) => item.notes.forEach((note) => used.add(note.c)));
  return used;
}

function renderPager(): void {
  const song = state.song!;
  const holder = byId("tk-pages");
  holder.replaceChildren();
  const chip = (label: string, action: () => void, options: { pressed?: boolean; disabled?: boolean; tip?: string; plain?: boolean } = {}): void => {
    const button = document.createElement(options.plain ? "span" : "button");
    button.className = "tk-chip" + (options.plain ? " plain" : "");
    button.textContent = label;
    if (button instanceof HTMLButtonElement) {
      button.type = "button";
      button.disabled = Boolean(options.disabled);
      button.addEventListener("click", action);
    }
    if (options.tip) {
      button.title = options.tip;
      button.setAttribute("aria-label", options.tip);
    }
    if (options.pressed !== undefined) button.setAttribute("aria-pressed", String(options.pressed));
    holder.append(button);
  };
  chip("‹", () => switchPattern(state.pat - 1), { disabled: state.pat <= 0, tip: t("the pattern before") });
  chip(t("pattern {n}/{total}", { n: state.pat + 1, total: song.patterns.length }), () => undefined, { plain: true, tip: t("{rows} rows", { rows: pattern().rows }) });
  chip("›", () => switchPattern(state.pat + 1), { disabled: state.pat >= song.patterns.length - 1, tip: t("the pattern after") });
  if (pageCount() > 1) {
    const size = perPage();
    for (let at = 0; at < pageCount(); at++) {
      chip(`V${at * size + 1}-${Math.min(voiceCount(), (at + 1) * size)}`, () => {
        page = at;
        renderNotes();
      }, { pressed: at === page, tip: t("voices {from} to {to}", { from: at * size + 1, to: Math.min(voiceCount(), (at + 1) * size) }) });
    }
  }
}

function switchPattern(index: number): void {
  const song = state.song!;
  state.pat = Math.max(0, Math.min(song.patterns.length - 1, index));
  state.sel = null;
  renderNotes();
}

function cellContent(cell: HTMLElement, note: TrackerNote | undefined): void {
  cell.replaceChildren();
  cell.classList.toggle("has", Boolean(note));
  cell.classList.toggle("cut", note?.n === NOTE_CUT);
  cell.classList.remove("high");
  if (!note) {
    cell.textContent = "···";
    return;
  }
  const song = state.song!;
  const name = document.createElement("span");
  name.className = "n";
  name.textContent = noteName(note.n);
  cell.append(name);
  if (note.n !== NOTE_CUT) {
    const sound = document.createElement("span");
    sound.className = "s";
    sound.textContent = String(note.s + 1);
    cell.append(sound);
    const own = song.sounds[note.s];
    if (own && note.n > maxNoteFor(own.rate)) cell.classList.add("high");
    if (note.v >= 0) {
      const volume = document.createElement("span");
      volume.className = "v";
      volume.textContent = String(note.v);
      cell.append(volume);
    }
  }
  if (note.fx) {
    const effect = document.createElement("span");
    effect.className = "x";
    effect.textContent = `${note.fx}${(note.fp ?? 0).toString(16).toUpperCase().padStart(2, "0")}`;
    cell.append(effect);
  }
}

function describe(r: number, c: number, note: TrackerNote | undefined): string {
  return note ? t("voice {c} row {r}: {note}", { c: c + 1, r, note: noteName(note.n) }) : t("voice {c} row {r}: empty", { c: c + 1, r });
}

export function renderNotes(): void {
  if (!state.song) return;
  const song = state.song;
  page = Math.min(page, pageCount() - 1);
  renderPager();
  const size = perPage();
  const first = page * size;
  const columns = Math.min(size, voiceCount() - first);
  const holder = grid();
  const keep = holder.scrollTop;
  holder.style.setProperty("--cols", String(columns));
  holder.replaceChildren();
  const used = usedVoices();
  const limit = voiceLimit(song);
  const corner = document.createElement("div");
  corner.className = "tk-h";
  holder.append(corner);
  for (let c = first; c < first + columns; c++) {
    const head = document.createElement("div");
    head.className = "tk-h" + (used.has(c) ? " used" : "") + (c >= limit ? " over" : "");
    head.textContent = `V${c + 1}`;
    head.title = c >= limit ? t("the SNES cannot play this voice") : "";
    holder.append(head);
  }
  const current = pattern();
  const lookup = new Map(current.notes.map((note) => [`${note.r}:${note.c}`, note]));
  for (let r = 0; r < current.rows; r++) {
    const number = document.createElement("div");
    number.className = "tk-rn" + (r % 4 === 0 ? " beat" : "");
    number.dataset.r = String(r);
    number.textContent = String(r).padStart(2, "0");
    holder.append(number);
    for (let c = first; c < first + columns; c++) {
      const cell = document.createElement("button");
      cell.type = "button";
      cell.className = "tk-cell" + (r % 4 === 0 ? " beat" : "") + (state.sel?.r === r && state.sel.c === c ? " sel" : "");
      cell.dataset.r = String(r);
      cell.dataset.c = String(c);
      const note = lookup.get(`${r}:${c}`);
      cellContent(cell, note);
      cell.setAttribute("aria-label", describe(r, c, note));
      holder.append(cell);
    }
  }
  holder.scrollTop = keep;
  playingRow = -1;
  if (!sheet().hidden) renderSheet();
}

// redraws one square after an edit, so the grid does not jump
function paintCell(r: number, c: number): void {
  const cell = grid().querySelector<HTMLElement>(`.tk-cell[data-r="${r}"][data-c="${c}"]`);
  if (!cell) return;
  const note = noteAt(r, c);
  cellContent(cell, note);
  cell.setAttribute("aria-label", describe(r, c, note));
}

function select(r: number, c: number, open: boolean): void {
  state.sel = { r, c };
  grid().querySelectorAll(".tk-cell.sel").forEach((cell) => cell.classList.remove("sel"));
  const cell = grid().querySelector<HTMLElement>(`.tk-cell[data-r="${r}"][data-c="${c}"]`);
  cell?.classList.add("sel");
  const note = noteAt(r, c);
  if (note && note.n !== NOTE_CUT) state.pen = note.s;
  if (open) {
    sheet().hidden = false;
    byId("tk-editor").classList.add("sheet-open");
    renderSheet();
  } else if (!sheet().hidden) renderSheet();
  if (cell) keepVisible(cell);
  if (open && cell) liftAbove(cell);
}

// the sheet covers the bottom of the screen, so the square being edited is scrolled up above it
function liftAbove(cell: HTMLElement): void {
  const view = byId("view-tracker");
  const top = Math.max(0, view.getBoundingClientRect().top) + 150;
  const bottom = window.innerHeight * 0.38;
  const rect = cell.getBoundingClientRect();
  if (rect.bottom > bottom) view.scrollTop += rect.bottom - bottom + 8;
  else if (rect.top < top) view.scrollTop -= top - rect.top;
}

function keepVisible(element: HTMLElement): void {
  const holder = grid();
  if (element.offsetTop < holder.scrollTop + 40) holder.scrollTop = element.offsetTop - 40;
  else if (element.offsetTop + element.offsetHeight > holder.scrollTop + holder.clientHeight) holder.scrollTop = element.offsetTop + element.offsetHeight - holder.clientHeight;
}

export function closeSheet(): void {
  sheet().hidden = true;
  byId("tk-editor").classList.remove("sheet-open");
}

// --- the note sheet

function renderSheet(): void {
  const song = state.song;
  if (!song || !state.sel) return;
  const { r, c } = state.sel;
  const note = noteAt(r, c);
  byId("tk-sheet-title").textContent = t("voice {c}, row {r}", { c: c + 1, r });
  byId("tk-sheet-oct").textContent = t("octave {n}", { n: state.octave });
  const sounds = byId("tk-sheet-sounds");
  sounds.replaceChildren();
  state.pen = Math.min(state.pen, Math.max(0, song.sounds.length - 1));
  song.sounds.forEach((sound, index) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "tk-chip sound";
    chip.setAttribute("aria-pressed", String(index === state.pen));
    chip.textContent = `${index + 1} ${sound.name}`;
    chip.addEventListener("click", () => {
      state.pen = index;
      if (note && note.n !== NOTE_CUT) edit((draft) => {
        const target = draft.patterns[state.pat].notes.find((item) => item.r === r && item.c === c);
        if (target) target.s = index;
      }, "notes");
      else renderSheet();
      previewNote(sound, state.octave * 12, earSnes());
    });
    sounds.append(chip);
  });
  if (!song.sounds.length) {
    const empty = document.createElement("span");
    empty.className = "hint";
    empty.textContent = t("no sounds yet, add one on the Sounds tab");
    sounds.append(empty);
  }
  renderPiano();
  const volume = byId<HTMLInputElement>("tk-sheet-vol");
  volume.value = String(note && note.v >= 0 ? note.v : 64);
  volume.disabled = !note || note.n === NOTE_CUT;
  byId("tk-sheet-vol-v").textContent = note && note.v >= 0 ? String(note.v) : t("sound's own");
  const effect = byId<HTMLSelectElement>("tk-fx");
  effect.value = note?.fx || "";
  effect.disabled = !note;
  const amount = byId<HTMLInputElement>("tk-fp");
  const chosen = effectById(note?.fx);
  amount.disabled = !chosen;
  amount.min = String(chosen?.min ?? 0);
  amount.max = String(chosen?.max ?? 0);
  amount.value = chosen ? String(note?.fp ?? chosen.start) : "";
  byId("tk-fx-hint").textContent = chosen ? t(chosen.detail) : "";
}

function renderPiano(): void {
  const song = state.song!;
  const limitNote = song.mode === "snes" && song.sounds[state.pen] ? maxNoteFor(song.sounds[state.pen].rate) : MAX_NOTE;
  const piano = byId("tk-piano");
  piano.replaceChildren();
  const key = (semitone: number, black: boolean, at = 0): void => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = black ? "tk-bk" : "tk-wk";
    button.dataset.k = String(semitone);
    const n = state.octave * 12 + semitone;
    button.disabled = n > limitNote || n > MAX_NOTE;
    button.setAttribute("aria-label", noteName(n));
    if (black) button.style.left = `${(at / 7) * 100}%`;
    else button.textContent = KEY_NAMES[semitone];
    piano.append(button);
  };
  WHITE.forEach((k) => key(k, false));
  BLACK.forEach(([k, at]) => key(k, true, at));
}

function putNote(n: number, advance: boolean): void {
  const song = state.song;
  if (!song || !state.sel) return;
  const { r, c } = state.sel;
  if (n > MAX_NOTE || n < 0) return;
  const sound = song.sounds[state.pen];
  if (!sound) return;
  if (song.mode === "snes" && n > maxNoteFor(sound.rate)) return;
  edit((draft) => {
    const notes = draft.patterns[state.pat].notes;
    const found = notes.find((item) => item.r === r && item.c === c);
    if (found) {
      found.n = n;
      found.s = state.pen;
    } else notes.push({ r, c, n, s: state.pen, v: -1 });
  }, "quiet");
  paintCell(r, c);
  previewNote(sound, n, earSnes());
  if (advance) {
    const next = Math.min(pattern().rows - 1, r + 1);
    select(next, c, false);
  } else renderSheet();
}

function stopNote(advance: boolean): void {
  if (!state.sel) return;
  const { r, c } = state.sel;
  edit((draft) => {
    const notes = draft.patterns[state.pat].notes;
    const found = notes.find((item) => item.r === r && item.c === c);
    if (found) {
      found.n = NOTE_CUT;
      found.s = 0;
      found.v = -1;
      delete found.fx;
      delete found.fp;
    } else notes.push({ r, c, n: NOTE_CUT, s: 0, v: -1 });
  }, "quiet");
  paintCell(r, c);
  if (advance) select(Math.min(pattern().rows - 1, r + 1), c, false);
  else renderSheet();
}

function clearNote(advance: boolean): void {
  if (!state.sel) return;
  const { r, c } = state.sel;
  if (noteAt(r, c)) edit((draft) => {
    draft.patterns[state.pat].notes = draft.patterns[state.pat].notes.filter((item) => !(item.r === r && item.c === c));
  }, "quiet");
  paintCell(r, c);
  if (advance) select(Math.min(pattern().rows - 1, r + 1), c, false);
  else renderSheet();
}

// --- the keyboard

export function handleKey(event: KeyboardEvent): boolean {
  const song = state.song;
  if (!song || event.ctrlKey || event.metaKey || event.altKey) return false;
  const key = event.key.toLowerCase();
  const target = event.target as HTMLElement | null;
  if (target?.closest("input, textarea, select")) return false;
  const move = (dr: number, dc: number): boolean => {
    const first = state.sel || { r: 0, c: page * perPage() };
    const r = Math.max(0, Math.min(pattern().rows - 1, first.r + dr));
    const c = Math.max(0, Math.min(voiceCount() - 1, first.c + dc));
    const size = perPage();
    if (Math.floor(c / size) !== page) {
      page = Math.floor(c / size);
      state.sel = { r, c };
      renderNotes();
    } else select(r, c, !sheet().hidden);
    return true;
  };
  if (key === "arrowup") return move(-1, 0);
  if (key === "arrowdown") return move(1, 0);
  if (key === "arrowleft") return move(0, -1);
  if (key === "arrowright") return move(0, 1);
  if (key === "escape" && !sheet().hidden) {
    closeSheet();
    return true;
  }
  if (key === "[" || key === "]") {
    state.octave = Math.max(0, Math.min(9, state.octave + (key === "]" ? 1 : -1)));
    if (!sheet().hidden) renderSheet();
    return true;
  }
  if (!state.sel && !LOWER.includes(key) && !UPPER.includes(key) && key !== "1") return false;
  if (key === "delete" || key === "backspace") {
    clearNote(false);
    return true;
  }
  if (key === "1") {
    if (!state.sel) select(0, page * perPage(), false);
    stopNote(true);
    return true;
  }
  const low = LOWER.indexOf(key);
  const high = UPPER.indexOf(key);
  if (low < 0 && high < 0) return false;
  if (!state.sel) select(0, page * perPage(), false);
  putNote(state.octave * 12 + (low >= 0 ? low : high + 12), true);
  return true;
}

export function setupNotes(options: { ear: () => boolean }): void {
  earSnes = options.ear;
  const effect = byId<HTMLSelectElement>("tk-fx");
  effect.replaceChildren(new Option(t("none"), ""), ...SAFE_EFFECTS.map((item) => new Option(t(item.label), item.id)));

  grid().addEventListener("click", (event) => {
    const cell = (event.target as HTMLElement).closest<HTMLElement>(".tk-cell");
    if (cell) select(Number(cell.dataset.r), Number(cell.dataset.c), true);
  });
  byId("tk-piano").addEventListener("click", (event) => {
    const key = (event.target as HTMLElement).closest<HTMLElement>("[data-k]");
    if (key && !(key as HTMLButtonElement).disabled) putNote(state.octave * 12 + Number(key.dataset.k), false);
  });
  byId("tk-oct-down").addEventListener("click", () => {
    state.octave = Math.max(0, state.octave - 1);
    renderSheet();
  });
  byId("tk-oct-up").addEventListener("click", () => {
    state.octave = Math.min(9, state.octave + 1);
    renderSheet();
  });
  byId("tk-sheet-cut").addEventListener("click", () => stopNote(false));
  byId("tk-sheet-clear").addEventListener("click", () => clearNote(false));
  byId("tk-sheet-done").addEventListener("click", closeSheet);
  byId<HTMLInputElement>("tk-sheet-vol").addEventListener("input", (event) => {
    if (!state.sel) return;
    const { r, c } = state.sel;
    const value = Number((event.target as HTMLInputElement).value);
    edit((draft) => {
      const found = draft.patterns[state.pat].notes.find((item) => item.r === r && item.c === c);
      if (found) found.v = value >= 64 ? -1 : value;
    }, "quiet", "volume");
    byId("tk-sheet-vol-v").textContent = value >= 64 ? t("sound's own") : String(value);
    paintCell(r, c);
  });
  effect.addEventListener("change", () => {
    if (!state.sel) return;
    const { r, c } = state.sel;
    const chosen = effectById(effect.value);
    edit((draft) => {
      const found = draft.patterns[state.pat].notes.find((item) => item.r === r && item.c === c);
      if (!found) return;
      if (chosen) {
        found.fx = chosen.id;
        found.fp = chosen.start;
      } else {
        delete found.fx;
        delete found.fp;
      }
    }, "quiet");
    paintCell(r, c);
    renderSheet();
  });
  byId<HTMLInputElement>("tk-fp").addEventListener("input", (event) => {
    if (!state.sel) return;
    const { r, c } = state.sel;
    const raw = Number((event.target as HTMLInputElement).value);
    edit((draft) => {
      const found = draft.patterns[state.pat].notes.find((item) => item.r === r && item.c === c);
      const chosen = effectById(found?.fx);
      if (found && chosen) found.fp = Math.max(chosen.min, Math.min(chosen.max, Math.round(Number.isFinite(raw) ? raw : chosen.start)));
    }, "quiet", "effect");
    paintCell(r, c);
  });
  window.matchMedia("(min-width: 760px)").addEventListener("change", () => state.song && renderNotes());
}

// the green row. when the song moves to another pattern the grid follows it
export function showStep(step: Step | null): void {
  const holder = grid();
  if (playingRow >= 0) holder.querySelectorAll(`[data-r="${playingRow}"]`).forEach((element) => element.classList.remove("play"));
  playingRow = -1;
  if (!step || !state.song) return;
  if (step.pattern !== state.pat) {
    state.pat = step.pattern;
    state.sel = null;
    renderNotes();
  }
  playingRow = step.row;
  const elements = holder.querySelectorAll<HTMLElement>(`[data-r="${step.row}"]`);
  elements.forEach((element) => element.classList.add("play"));
  if (elements[0]) keepVisible(elements[0]);
}
